import React from 'react';

import { Route } from 'react-router-dom';

import { routes } from 'lane-shared/domains/visitorManagement/index';
import { hasPermission } from 'lane-shared/helpers';
import { ICON_SET_FONTAWESOME } from 'lane-shared/helpers/constants/icons';
import { PERMISSION_KEYS } from 'constants-permissions';
import { SIDE_BAR_LINKS_ORDER } from 'lane-shared/helpers/constants/sidebarLinksOrder';
import { useFlag } from 'lane-shared/hooks';
import i18n from 'localization';
import { FeatureFlag } from 'constants-flags';
import { UserType } from 'lane-shared/types/User';

import { VisitorConfigurationPage } from '../pages/visitor-configuration';
import { VisitorFormsPage } from '../pages/visitor-forms';
import { VisitorLogPage } from '../pages/visitor-log';
import { VisitorPassDetailsPage } from '../pages/visitorpass-details';
import { VisitorRecurrenceDetailsPage } from '../pages/visitorrecurrencepass-details';
import { VisitorWatchlistPage } from '../pages/visitor-watchlist';
import { VisitorWatchlistFormPage } from '../pages/visitor-watchlist/visitor-watchlist-form';
import { WatchlistDetailsPage } from '../pages/watchlist-details';
import { SidebarLinkType } from 'hooks/useChannelSidebarLinks';
import { Channel } from 'packages/lane-shared/types/ChannelType';

enum LinkOrder {
  Log,
  Forms,
  Configuration,
  PassDetails,
  Watchlist,
}

export enum VisitorManagementPageName {
  VisitorLog = 'visitorLog',
  VisitorForms = 'visitorForms',
  VisitorConfiguration = 'visitorConfiguration',
  VisitorPassDetails = 'visitorPassDetails',
  VisitorRecurrenceDetails = 'visitorRecurrenceDetails',
  VisitorWatchlist = 'visitorWatchlist',
  VisitorWatchlistForm = 'visitorWatchlistForm',
  VisitorWatchlistDetails = 'visitorWatchlistDetails',
}

type VisitorManagementPage = {
  key: VisitorManagementPageName;
  name: () => string;
  headerMessage: () => string;
  order: LinkOrder;
  Page: React.FC<{ channel: Channel | any; user: UserType }>;
  path: string;
};

export const VisitorLogsPageConfig: VisitorManagementPage = {
  key: VisitorManagementPageName.VisitorLog,
  name: () => i18n.t('web.admin.visitorManagement.navigation.visitorLog'),
  headerMessage: () =>
    i18n.t('web.admin.visitorManagement.navigation.headerMessage'),
  order: LinkOrder.Log,
  Page: VisitorLogPage,
  path: routes.visitorLog,
};

const VisitorFormsPageConfig: VisitorManagementPage = {
  key: VisitorManagementPageName.VisitorForms,
  name: () =>
    i18n.t('web.admin.visitorManagement.navigation.registrationForms'),
  headerMessage: () =>
    i18n.t('web.admin.visitorManagement.navigation.headerMessage'),
  order: LinkOrder.Forms,
  Page: VisitorFormsPage,
  path: routes.registrationForm,
};

export const VisitorConfigurationPageConfig: VisitorManagementPage = {
  key: VisitorManagementPageName.VisitorConfiguration,
  name: () =>
    i18n.t('web.admin.visitorManagement.navigation.visitorConfiguration'),
  headerMessage: () =>
    i18n.t('web.admin.visitorManagement.navigation.headerMessage'),
  order: LinkOrder.Configuration,
  Page: VisitorConfigurationPage,
  path: routes.visitorConfiguration,
};

export const VisitorPassDetailsPageConfig: VisitorManagementPage = {
  key: VisitorManagementPageName.VisitorPassDetails,
  name: () => i18n.t('web.admin.visitorManagement.navigation.passDetails'),
  headerMessage: () =>
    i18n.t('web.admin.visitorManagement.navigation.headerMessage'),
  order: LinkOrder.PassDetails,
  Page: VisitorPassDetailsPage,
  path: routes.visitorPassDetails,
};

export const VisitorRecurrenceDetailsPageConfig: VisitorManagementPage = {
  key: VisitorManagementPageName.VisitorRecurrenceDetails,
  name: () =>
    i18n.t('web.admin.visitorManagement.navigation.recurrenceDetails'),
  headerMessage: () =>
    i18n.t('web.admin.visitorManagement.navigation.headerMessage'),
  order: LinkOrder.PassDetails,
  Page: VisitorRecurrenceDetailsPage,
  path: routes.recurrenceDetails,
};

export const VisitorWatchlistPageConfig: VisitorManagementPage = {
  key: VisitorManagementPageName.VisitorWatchlist,
  name: () => i18n.t('web.admin.visitorManagement.navigation.watchlist'),
  headerMessage: () =>
    i18n.t('web.admin.visitorManagement.navigation.headerMessage'),
  order: LinkOrder.Watchlist,
  Page: VisitorWatchlistPage,
  path: routes.visitorWatchlist,
};

export const VisitorWatchlistFormPageConfig: VisitorManagementPage = {
  key: VisitorManagementPageName.VisitorWatchlistForm,
  name: () => i18n.t('web.admin.channel.visitor.watchlist.title'),
  headerMessage: () =>
    i18n.t('web.admin.visitorManagement.navigation.headerMessage'),
  order: LinkOrder.Watchlist,
  Page: VisitorWatchlistFormPage,
  path: routes.visitorWatchlistForm,
};

export const VisitorWatchlistDetailsPageConfig: VisitorManagementPage = {
  key: VisitorManagementPageName.VisitorWatchlistDetails,
  name: () => i18n.t('web.admin.channel.visitor.watchlist.details'),
  headerMessage: () =>
    i18n.t('web.admin.visitorManagement.navigation.headerMessage'),
  order: LinkOrder.Watchlist,
  Page: WatchlistDetailsPage,
  path: routes.visitorWatchlistDetails,
};

export const visitorManagementPages: Record<
  VisitorManagementPageName,
  VisitorManagementPage
> = {
  [VisitorManagementPageName.VisitorLog]: VisitorLogsPageConfig,

  [VisitorManagementPageName.VisitorForms]: VisitorFormsPageConfig,

  [VisitorManagementPageName.VisitorConfiguration]:
    VisitorConfigurationPageConfig,

  [VisitorManagementPageName.VisitorPassDetails]: VisitorPassDetailsPageConfig,

  [VisitorManagementPageName.VisitorRecurrenceDetails]:
    VisitorRecurrenceDetailsPageConfig,

  [VisitorManagementPageName.VisitorWatchlist]: VisitorWatchlistPageConfig,

  [VisitorManagementPageName.VisitorWatchlistForm]:
    VisitorWatchlistFormPageConfig,

  [VisitorManagementPageName.VisitorWatchlistDetails]:
    VisitorWatchlistDetailsPageConfig,
};

const hasAnyPermission = (
  user: UserType,
  permissions: string[],
  channelId: string
) => {
  return (
    user.isSuperUser ||
    (channelId && hasPermission(user.roles, permissions, channelId))
  );
};

export const VisitorManagementRoutes = ({
  urlPath,
  channel,
  user,
}: {
  urlPath: string;
  channel?: Channel | any;
  user: UserType;
}): React.ReactElement | null => {
  const visitorManagementFlag = useFlag(FeatureFlag.VisitorManagement, false);

  if (!visitorManagementFlag) return null;

  if (!channel?.settings?.hasVisitorManagementEnabled) return null;

  const entries: [string, VisitorManagementPage][] = [];

  if (
    hasAnyPermission(
      user,
      [
        PERMISSION_KEYS.PERMISSION_VISITOR_MANAGEMENT_LOGS_READ,
        PERMISSION_KEYS.PERMISSION_VISITOR_MANAGEMENT_LOGS_READ_ALL,
      ],
      channel._id
    )
  ) {
    entries.push([VisitorManagementPageName.VisitorLog, VisitorLogsPageConfig]);
    entries.push([
      VisitorManagementPageName.VisitorPassDetails,
      VisitorPassDetailsPageConfig,
    ]);
    entries.push([
      VisitorManagementPageName.VisitorRecurrenceDetails,
      VisitorRecurrenceDetailsPageConfig,
    ]);
  }

  const hasAdminPermission = hasAnyPermission(
    user,
    [PERMISSION_KEYS.PERMISSION_ADMIN],
    channel._id
  );
  const hasConfigureWatchlistPermission = hasAnyPermission(
    user,
    [PERMISSION_KEYS.PERMISSION_VISITORS_WATCHLIST_CONFIGURE],
    channel._id
  );

  if (hasAdminPermission) {
    // these links will be later controlled by specific permissions once they are defined
    entries.push([
      VisitorManagementPageName.VisitorForms,
      VisitorFormsPageConfig,
    ]);

    entries.push([
      VisitorManagementPageName.VisitorConfiguration,
      VisitorConfigurationPageConfig,
    ]);
  } else if (hasConfigureWatchlistPermission) {
    // Visitor Configuration: show if user has configure permission for watchlist (but not admin)
    entries.push([
      VisitorManagementPageName.VisitorConfiguration,
      VisitorConfigurationPageConfig,
    ]);
  }

  // Watchlist routes: view permission allows access to list and details pages (manage permission also includes view)
  if (
    hasAnyPermission(
      user,
      [
        PERMISSION_KEYS.PERMISSION_VISITORS_WATCHLIST_VIEW,
        PERMISSION_KEYS.PERMISSION_VISITORS_WATCHLIST_MANAGE,
      ],
      channel._id
    )
  ) {
    entries.push([
      VisitorManagementPageName.VisitorWatchlist,
      VisitorWatchlistPageConfig,
    ]);
    entries.push([
      VisitorManagementPageName.VisitorWatchlistDetails,
      VisitorWatchlistDetailsPageConfig,
    ]);
  }

  // Watchlist form route: manage permission required for create/edit
  if (
    hasAnyPermission(
      user,
      [PERMISSION_KEYS.PERMISSION_VISITORS_WATCHLIST_MANAGE],
      channel._id
    )
  ) {
    entries.push([
      VisitorManagementPageName.VisitorWatchlistForm,
      VisitorWatchlistFormPageConfig,
    ]);
  }

  return (
    <>
      {entries.map(([key, { Page, path }]) => {
        return (
          <Route key={key} exact path={`${urlPath}/${path}`}>
            <Page channel={channel} user={user} />
          </Route>
        );
      })}
    </>
  );
};

export const useVisitorManagementNavigation = (
  url: string,
  channelId: string,
  user: UserType
) => {
  const navigation = {
    icon: 'id-card-alt',
    iconSet: ICON_SET_FONTAWESOME,
    name: i18n.t('web.admin.visitorManagement.navigation.headerMessage'),
    isCategory: true,
    order: SIDE_BAR_LINKS_ORDER.VISITOR_MANAGEMENT,
    links: [],
  };

  // Early return if no channelId or user
  if (!channelId || !user) {
    return navigation;
  }

  const links: SidebarLinkType[] = [];

  if (
    hasAnyPermission(
      user,
      [
        PERMISSION_KEYS.PERMISSION_VISITOR_MANAGEMENT_LOGS_READ,
        PERMISSION_KEYS.PERMISSION_VISITOR_MANAGEMENT_LOGS_READ_ALL,
      ],
      channelId
    )
  ) {
    links.push({
      exact: true,
      route: `${url}/${VisitorLogsPageConfig.path}`,
      name: VisitorLogsPageConfig.name(),
      headerMessage: VisitorLogsPageConfig.headerMessage(),
      order: VisitorLogsPageConfig.order,
    });
  }

  const hasAdminPermission = hasAnyPermission(
    user,
    [PERMISSION_KEYS.PERMISSION_ADMIN],
    channelId
  );
  const hasConfigureWatchlistPermission = hasAnyPermission(
    user,
    [PERMISSION_KEYS.PERMISSION_VISITORS_WATCHLIST_CONFIGURE],
    channelId
  );

  if (hasAdminPermission) {
    // these links will be later controlled by specific permissions once they are defined
    links.push({
      exact: true,
      route: `${url}/${VisitorFormsPageConfig.path}`,
      name: VisitorFormsPageConfig.name(),
      headerMessage: VisitorFormsPageConfig.headerMessage(),
      order: VisitorFormsPageConfig.order,
    });

    links.push({
      exact: true,
      route: `${url}/${VisitorConfigurationPageConfig.path}`,
      name: VisitorConfigurationPageConfig.name(),
      headerMessage: VisitorConfigurationPageConfig.headerMessage(),
      order: VisitorConfigurationPageConfig.order,
    });
  } else if (hasConfigureWatchlistPermission) {
    // Visitor Configuration: show if user has configure permission for watchlist (but not admin)
    links.push({
      exact: true,
      route: `${url}/${VisitorConfigurationPageConfig.path}`,
      name: VisitorConfigurationPageConfig.name(),
      headerMessage: VisitorConfigurationPageConfig.headerMessage(),
      order: VisitorConfigurationPageConfig.order,
    });
  }

  // Watchlist tab: requires view permission (manage permission also includes view)
  if (
    hasAnyPermission(
      user,
      [
        PERMISSION_KEYS.PERMISSION_VISITORS_WATCHLIST_VIEW,
        PERMISSION_KEYS.PERMISSION_VISITORS_WATCHLIST_MANAGE,
      ],
      channelId
    )
  ) {
    links.push({
      exact: true,
      route: `${url}/${VisitorWatchlistPageConfig.path}`,
      name: VisitorWatchlistPageConfig.name(),
      headerMessage: VisitorWatchlistPageConfig.headerMessage(),
      order: VisitorWatchlistPageConfig.order,
    });
  }

  return {
    ...navigation,
    links,
  };
};
